// src/components/DataTransferMenu.jsx
// Export, template and import for ONE entity, mounted in that entity's own page
// header.
//
// WHY THIS EXISTS RATHER THAN THE SETTINGS PANEL ALONE
// P0.14 put all of this in Profile & Settings → Data behind an entity picker.
// That works, but it inverts the real flow: a user standing on the Accounts page
// wanting those accounts out had to navigate to Settings and re-select "Accounts"
// from a dropdown they had just navigated away from. The picker existed because
// the panel was centralised, not because anyone needed to choose.
//
// Here the entity is fixed by a prop, so the picker is gone and every action is
// one click on data already in view.
//
// ONE COMPONENT, FIVE MOUNTS. The five pages have different header markup
// (`header-right`, `pipeline-header-right`, `leads-header-right`, …) but identical
// needs. Copying the logic five times is how the export on one page silently
// drifts from the export on another; this cannot.
//
// WHY A POPOVER AND A MODAL
// Four of the five headers already carry actions (+ New, ↻ Refresh). Three more
// buttons per page would crowd every header and push the primary action aside —
// so one button opens a popover with three items. Import then opens a MODAL,
// because its staged flow (upload → preview → error table → commit) would
// otherwise take over the host page.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';
import { downloadFile } from '../lib/download';
import './DataTransferMenu.css';

// A CSV of a few thousand rows is far under this; the cap stops a mis-selected
// video being read into memory and POSTed.
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * @param {string}   entity     registry key — accounts | contacts | leads | opportunities | tasks
 * @param {string}   label      human name for this entity, e.g. "Accounts"
 * @param {function} [onImported] called after a successful commit so the host page refetches
 */
export default function DataTransferMenu({ entity, label, onImported }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);        // 'export' | 'template' | null
  const [message, setMessage] = useState(null);  // { kind, text }
  const [importOpen, setImportOpen] = useState(false);
  const rootRef = useRef(null);

  // Close the popover on an outside click or Escape. Without this it stays open
  // behind whatever the user clicks next.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const doDownload = useCallback(async (kind) => {
    setBusy(kind);
    setMessage(null);
    const isTemplate = kind === 'template';
    // `entity` comes from the prop, never from UI state — a page cannot export
    // someone else's entity.
    const path = `/api/data/export?entity=${encodeURIComponent(entity)}${isTemplate ? '&template=1' : ''}`;
    const out = await downloadFile(path, `${entity}${isTemplate ? '-template' : ''}.csv`);
    setBusy(null);
    setOpen(false);
    setMessage(out.ok
      ? { kind: 'ok', text: `Downloaded ${out.filename}` }
      : { kind: 'error', text: out.error });
  }, [entity]);

  // The toast is informational; it must not linger over the page.
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => setMessage(null), 6000);
    return () => clearTimeout(t);
  }, [message]);

  return (
    <div className="dtm-root" ref={rootRef}>
      <button
        type="button"
        className="dtm-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Import or export ${label.toLowerCase()}`}
      >
        ⇅ Data
      </button>

      {open && (
        <div className="dtm-popover" role="menu">
          <button
            type="button" role="menuitem" className="dtm-item"
            onClick={() => doDownload('export')} disabled={busy !== null}
          >
            <span className="dtm-item-icon" aria-hidden="true">⬇</span>
            <span>
              <span className="dtm-item-title">
                {busy === 'export' ? 'Preparing…' : `Export ${label}`}
              </span>
              <span className="dtm-item-sub">Every record, as a CSV</span>
            </span>
          </button>

          <button
            type="button" role="menuitem" className="dtm-item"
            onClick={() => doDownload('template')} disabled={busy !== null}
          >
            <span className="dtm-item-icon" aria-hidden="true">📄</span>
            <span>
              <span className="dtm-item-title">
                {busy === 'template' ? 'Preparing…' : 'Download template'}
              </span>
              <span className="dtm-item-sub">Blank file with the importable columns</span>
            </span>
          </button>

          <button
            type="button" role="menuitem" className="dtm-item"
            onClick={() => { setOpen(false); setImportOpen(true); }}
          >
            <span className="dtm-item-icon" aria-hidden="true">⬆</span>
            <span>
              <span className="dtm-item-title">Import {label}</span>
              <span className="dtm-item-sub">Checked before anything is saved</span>
            </span>
          </button>
        </div>
      )}

      {message && (
        <div className={`dtm-toast dtm-toast--${message.kind}`} role="status">{message.text}</div>
      )}

      {importOpen && (
        <ImportModal
          entity={entity}
          label={label}
          onClose={() => setImportOpen(false)}
          onImported={onImported}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The import flow, in a modal.
//
// Staged exactly as P0.14's Settings panel and the older ingest tab are
// (upload | validating | preview | committing | done), so the app has one import
// idiom rather than three. The rule the staging enforces: NOTHING IS WRITTEN
// UNTIL THE USER APPROVES THE PREVIEW — and that is a property of
// api/data/import.js, whose validate mode has no insert path, not a promise this
// component makes.
// ---------------------------------------------------------------------------
function ImportModal({ entity, label, onClose, onImported }) {
  const [stage, setStage] = useState('upload');
  const [file, setFile] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [result, setResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [error, setError] = useState(null);

  const busy = stage === 'validating' || stage === 'committing';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const reset = () => {
    setStage('upload'); setFile(null); setCsvText('');
    setResult(null); setCommitResult(null); setError(null);
  };

  const onFile = async (chosen) => {
    if (!chosen) return;
    setError(null);

    if (chosen.size > MAX_BYTES) {
      setError(`That file is ${(chosen.size / 1048576).toFixed(1)} MB. The limit is 10 MB.`);
      return;
    }
    if (!/\.csv$/i.test(chosen.name)) {
      setError('That does not look like a .csv file. Export from Excel as CSV first.');
      return;
    }

    setFile(chosen);
    const text = await chosen.text();
    setCsvText(text);

    setStage('validating');
    try {
      const res = await apiFetch('/api/data/import', {
        method: 'POST',
        body: JSON.stringify({ entity, csvText: text, mode: 'validate', filename: chosen.name }),
      });
      const body = await res.json();
      if (!res.ok) {
        // A file-level rejection — a tenant_id column, a missing required column —
        // comes back as a 400 with a sentence the user can act on.
        setError(body.error || `The file could not be checked (HTTP ${res.status}).`);
        setStage('upload');
        return;
      }
      setResult(body);
      setStage('preview');
    } catch (err) {
      setError(err.message || 'The file could not be checked.');
      setStage('upload');
    }
  };

  const commit = async () => {
    setStage('committing');
    setError(null);
    try {
      // csvText is re-sent, not the validated rows: the server re-runs the whole
      // validation and never trusts what the browser echoes back.
      const res = await apiFetch('/api/data/import', {
        method: 'POST',
        body: JSON.stringify({ entity, csvText, mode: 'commit', filename: file?.name || null }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || `The import failed (HTTP ${res.status}).`);
        setStage('preview');
        return;
      }
      setCommitResult(body);
      setStage('done');
      // Refetch the host page so the imported rows are visible without a reload.
      if (body.inserted > 0 && typeof onImported === 'function') onImported();
    } catch (err) {
      setError(err.message || 'The import failed.');
      setStage('preview');
    }
  };

  const downloadErrors = async (runId) => {
    const out = await downloadFile(
      `/api/data/import-errors?runId=${encodeURIComponent(runId)}`, 'import-errors.csv');
    if (!out.ok) setError(out.error);
  };

  const counts = result?.counts;

  return (
    <div className="dtm-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="dtm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="dtm-modal-header">
          <h2 className="dtm-modal-title">Import {label}</h2>
          <button className="dtm-modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>

        <div className="dtm-modal-body">
          {error && <div className="dtm-message dtm-message--error" role="alert">{error}</div>}

          {(stage === 'upload' || stage === 'validating') && (
            <>
              <p className="dtm-lede">
                Upload a CSV to add {label.toLowerCase()}. Nothing is saved until you review
                what was found and confirm. Existing records are never overwritten.
              </p>
              <label className={`dtm-drop ${stage === 'validating' ? 'dtm-drop--busy' : ''}`}>
                <input
                  type="file" accept=".csv,text/csv" className="dtm-file-input"
                  disabled={stage === 'validating'}
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
                <span className="dtm-drop-icon" aria-hidden="true">📄</span>
                <span className="dtm-drop-text">
                  {stage === 'validating' ? 'Checking the file…' : 'Choose a CSV file'}
                </span>
                <span className="dtm-drop-hint">
                  Not sure of the format? Download the template first.
                </span>
              </label>
            </>
          )}

          {stage === 'preview' && counts && (
            <>
              <p className="dtm-lede">
                <strong>{file?.name}</strong> — checked, nothing saved yet.
              </p>
              <div className="dtm-counts">
                <Count n={counts.total} label="rows read" />
                <Count n={counts.valid} label="ready to import" tone="ok" />
                <Count n={counts.duplicates} label="already exist" tone="warn" />
                <Count n={counts.errorCount} label="problems" tone={counts.errorCount ? 'bad' : undefined} />
              </div>

              {result.ignored?.length > 0 && (
                <p className="dtm-note">
                  Calculated by the app and not changed: {result.ignored.join(', ')}.
                </p>
              )}

              {/* A blank Account Name imports the row unlinked, where a misspelled
                  one is a loud per-row error. Said plainly here so a file whose
                  Account Name column was never filled in does not read as clean. */}
              {result.unlinked > 0 && (
                <p className="dtm-note dtm-note--warn">
                  {result.unlinked} of these {result.unlinked === 1 ? 'rows has' : 'rows have'} no
                  Account Name and will import without being linked to an account. Import the
                  accounts first, then re-upload, if they should be linked.
                </p>
              )}

              {result.errors?.length > 0 && (
                <>
                  <h3 className="dtm-subhead">
                    Rows that will not be imported
                    {result.errorsTruncated && <span className="dtm-muted"> (first 200)</span>}
                  </h3>
                  <div className="dtm-table-wrap">
                    <table className="dtm-table">
                      <thead><tr><th>Row</th><th>Column</th><th>Value</th><th>Reason</th></tr></thead>
                      <tbody>
                        {result.errors.slice(0, 50).map((e, i) => (
                          <tr key={i}>
                            <td>{e.row ?? '—'}</td>
                            <td>{e.column || '—'}</td>
                            <td className="dtm-cell-value">{e.value || '—'}</td>
                            <td>{e.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {result.errors.length > 50 && (
                    <p className="dtm-note">
                      Showing 50 of {result.errors.length}. Import to get the full list as a CSV.
                    </p>
                  )}
                </>
              )}

              {!counts.valid && (
                <p className="dtm-note">
                  Every row was either a duplicate or had a problem. Fix the file and try again.
                </p>
              )}
            </>
          )}

          {stage === 'committing' && (
            <p className="dtm-lede">Importing… this can take a moment for a large file.</p>
          )}

          {stage === 'done' && commitResult && (
            <>
              {/* THE VERDICT FIRST (B132). Three neutral counts read the same
                  whether nine rows failed or every row did — which is how a
                  customer came to re-upload the same file seven times over four
                  days, each run importing nothing. Say which of the three
                  outcomes this was before showing the numbers. */}
              {commitResult.outcome === 'failed' && (
                <p className="dtm-verdict dtm-verdict-bad">
                  Nothing was imported. All {commitResult.counts?.total ?? commitResult.errorCount}{' '}
                  rows were rejected.
                  {commitResult.dominantCause && <> Most common reason — {commitResult.dominantCause}</>}
                </p>
              )}
              {commitResult.outcome === 'partial' && (
                <p className="dtm-verdict dtm-verdict-warn">
                  Imported {commitResult.inserted} of {commitResult.counts?.total ?? '—'} rows.
                  {commitResult.dominantCause && <> The rest — {commitResult.dominantCause}</>}
                </p>
              )}

              <div className="dtm-counts">
                <Count n={commitResult.inserted} label="imported" tone="ok" />
                <Count n={commitResult.skipped} label="skipped as duplicates" tone="warn" />
                <Count n={commitResult.errorCount} label="problems"
                  tone={commitResult.errorCount ? 'bad' : undefined} />
              </div>
              {!commitResult.runRecorded && (
                /* This message only appears when the audit insert failed — and a
                   wholesale RLS refusal fails the rows AND the audit row, so the
                   old copy ("The rows were imported, but…") asserted success
                   exactly when it was least likely to be true (B132.3). */
                <p className="dtm-note">
                  {commitResult.inserted > 0
                    ? `${commitResult.inserted} row${commitResult.inserted === 1 ? '' : 's'} imported, but this run `
                    : 'This run '}
                  could not be added to the history in Profile &amp; Settings → Data.
                </p>
              )}

              {/* A database rejection can only be discovered during the import —
                  validate writes nothing, so it cannot appear in the preview.
                  Showing it here means the reason is on screen, rather than a
                  count that sends the user to Settings to find out what broke. */}
              {commitResult.errors?.length > 0 && (
                <>
                  <h3 className="dtm-subhead">
                    Rows that were not imported
                    {commitResult.errorsTruncated && <span className="dtm-muted"> (first 200)</span>}
                  </h3>
                  <div className="dtm-table-wrap">
                    <table className="dtm-table">
                      <thead><tr><th>Row</th><th>Column</th><th>Value</th><th>Reason</th></tr></thead>
                      <tbody>
                        {commitResult.errors.slice(0, 50).map((e, i) => (
                          <tr key={i}>
                            <td>{e.row ?? '—'}</td>
                            <td>{e.column ?? '—'}</td>
                            <td className="dtm-cell-value">{e.value || '—'}</td>
                            <td>{e.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {commitResult.errors.length > 50 && (
                    <p className="dtm-note">
                      Showing 50 of {commitResult.errors.length}. The full list is in
                      Profile &amp; Settings → Data.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="dtm-modal-footer">
          {stage === 'preview' && (
            <>
              <button className="dtm-btn" onClick={reset}>Choose a different file</button>
              <button className="dtm-btn dtm-btn--primary" onClick={commit} disabled={!counts?.valid}>
                {counts?.valid
                  ? `Import ${counts.valid} ${counts.valid === 1 ? 'row' : 'rows'}`
                  : 'Nothing to import'}
              </button>
            </>
          )}

          {stage === 'done' && (
            <>
              {commitResult.errorCount > 0 && commitResult.runId && (
                <button className="dtm-btn" onClick={() => downloadErrors(commitResult.runId)}>
                  Download the rejected rows
                </button>
              )}
              <button className="dtm-btn" onClick={reset}>Import another file</button>
              <button className="dtm-btn dtm-btn--primary" onClick={onClose}>Done</button>
            </>
          )}

          {(stage === 'upload' || stage === 'validating') && (
            <button className="dtm-btn" onClick={onClose} disabled={busy}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Count({ n, label, tone }) {
  return (
    <div className={`dtm-count ${tone ? `dtm-count--${tone}` : ''}`}>
      <span className="dtm-count-n">{n}</span>
      <span className="dtm-count-label">{label}</span>
    </div>
  );
}
